/**
 * Sentry Frontend Integration
 * Observability & Analytics Infrastructure - Task 3.3
 *
 * Initializes Sentry error tracking with:
 * - PII scrubbing via beforeSend/beforeBreadcrumb hooks
 * - Feature flag context attachment
 * - Performance monitoring (10-20% sample rate)
 * - Graceful degradation if VITE_SENTRY_DSN missing
 */

import * as Sentry from "@sentry/react";
import type { RequestEventData } from "@sentry/core";
import { scrubPII } from "@vardic/shared/utils/pii-scrubbing";
import { getFrontendFeatureFlags } from "@vardic/shared/utils/feature-flag-context";

/**
 * Initialize Sentry for frontend error tracking and performance monitoring
 *
 * Configuration:
 * - DSN: VITE_SENTRY_DSN (EU region)
 * - Environment: import.meta.env.MODE (development/production)
 * - PII scrubbing: enabled via beforeSend/beforeBreadcrumb hooks
 * - Feature flags: attached to all errors
 * - Performance monitoring: 10-20% sample rate
 *
 * Graceful degradation: skips initialization if VITE_SENTRY_DSN missing
 */
export function initSentry(): Promise<void> {
  const envDsn: unknown = import.meta.env.VITE_SENTRY_DSN;
  const dsn = typeof envDsn === "string" ? envDsn : "";
  const envMode: unknown = import.meta.env.MODE;
  const environment = typeof envMode === "string" && envMode.length > 0
    ? envMode
    : "development";

  // Graceful degradation: skip if DSN not configured
  if (!dsn) {
    console.warn(
      "[Sentry] VITE_SENTRY_DSN not configured. Error tracking disabled.",
    );
    return Promise.resolve();
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release: import.meta.env.VITE_GIT_SHA,
      sendDefaultPii: false, // Critical for GDPR compliance
      // Performance monitoring: 10-20% sample rate
      tracesSampleRate: 0.15, // 15% - middle of 10-20% range
      // BrowserTracing is included by default in @sentry/react v7+

      // PII scrubbing: apply to all events before sending
      beforeSend: async (event) => {
        try {
          // Scrub PII from event data
          if (event.user) {
            event.user = scrubPII(event.user) as Sentry.User;
          }

          if (event.extra) {
            event.extra = scrubPII(event.extra) as Record<string, unknown>;
          }

          if (event.request) {
            event.request = scrubPII(event.request) as RequestEventData;
          }

          // Attach feature flag context
          const featureFlagContext = await getFrontendFeatureFlags();
          if (!event.contexts) {
            event.contexts = {};
          }
          event.contexts.featureFlags = featureFlagContext.flags;

          return event;
        } catch (error) {
          // Non-blocking: log error but still send event
          console.error("[Sentry] Error in beforeSend hook:", error);
          return event;
        }
      },

      // PII scrubbing: apply to breadcrumbs
      beforeBreadcrumb: (breadcrumb) => {
        try {
          if (breadcrumb.data) {
            breadcrumb.data = scrubPII(breadcrumb.data) as Record<
              string,
              unknown
            >;
          }
          return breadcrumb;
        } catch (error) {
          // Non-blocking: log error but still record breadcrumb
          console.error("[Sentry] Error in beforeBreadcrumb hook:", error);
          return breadcrumb;
        }
      },
    });
  } catch (error) {
    // Graceful degradation: log error but don't crash app
    console.error("[Sentry] Failed to initialize:", error);
  }

  return Promise.resolve();
}


/**
 * Sentry ErrorBoundary component (re-exported for convenience)
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

/**
 * Sentry captureException function (re-exported for convenience)
 */
export const captureException = Sentry.captureException;
